import { SignUpStatus } from "@is/constants";
import { ViewStateContainer } from "@is/contexts";
import { LoadMediaItem } from "@is/services/MediaManager";
import {
  ContentItem,
  MediaType,
  PreferenceOption,
  PriceRange,
  ProductItem,
  SearchTerm,
  Section,
  SuggestionItem,
} from "@is/types";

export type ContentViewState = {
  items: ContentItem[];
  searchTerms: SearchTerm[];
  initialSearchTerms: SearchTerm[];
  page: number;
  totalCount?: number;
  hasNextPage?: boolean;
  focusedItemId?: string;
  showPrimaryAttributeDrawer?: boolean;
  showBasketDrawer?: boolean;
  preloadMediaItems: LoadMediaItem[];
  suggestions: SuggestionItem[];
  animateLeavingEntryView?: boolean;
  selectedTab?: string;
  showLanguageAndCurrencyModal?: boolean;
  tabs?: PreferenceOption[];
  showSearchAndFooterDrawers?: boolean;
  showPrimaryAttributeButtons?: boolean;
  showEnlargedModal?: boolean;
  enlargedMedias: { initialIndex: number; medias: MediaType[] };
  lastScannedValue: string;
  sections: Section[];
  intersectedItemsIds: string[];
  showQRCodeModal: boolean;
  firstSectionOffset: number | null;
  qrCodeCheckout: {
    status: string;
    inProgress: boolean;
    isFailed: boolean;
  };
  showSettingsDrawer: boolean;
  isCheckoutEnabled: boolean;
  showStoreFavoritesModal: boolean;
  showSignUpModal: boolean;
  signUpStatusModal: {
    isOpen: boolean;
    status: SignUpStatus | null;
    errorMessage?: string;
  };
  externalPageDrawer: {
    isOpen: boolean;
    src: string | null;
  };
  quickAddToBasketItem: ProductItem | null;
  restoredParkedBasketName: string;
  hasProvidedPasscode: boolean;
  priceRange: PriceRange;
};

export type ContentViewStateContainer = ViewStateContainer<ContentViewState> & {
  updateItem: (itemId: string, data: Partial<ContentItem>) => void;
};

export const initialContentViewState: ContentViewState = {
  items: [],
  searchTerms: [],
  initialSearchTerms: [],
  page: 1,
  totalCount: 0,
  hasNextPage: false,
  focusedItemId: null,
  showPrimaryAttributeDrawer: false,
  showBasketDrawer: false,
  preloadMediaItems: [],
  suggestions: [],
  animateLeavingEntryView: false,
  selectedTab: null,
  showLanguageAndCurrencyModal: false,
  tabs: [],
  showSearchAndFooterDrawers: true,
  showPrimaryAttributeButtons: true,
  showEnlargedModal: false,
  enlargedMedias: { initialIndex: 0, medias: [] },
  lastScannedValue: null,
  sections: [],
  intersectedItemsIds: [],
  showQRCodeModal: false,
  firstSectionOffset: null,
  qrCodeCheckout: {
    status: "",
    inProgress: false,
    isFailed: false,
  },
  showSettingsDrawer: false,
  isCheckoutEnabled: false,
  showStoreFavoritesModal: false,
  showSignUpModal: false,
  signUpStatusModal: {
    isOpen: false,
    status: null,
    errorMessage: null,
  },
  externalPageDrawer: {
    isOpen: false,
    src: null,
  },
  quickAddToBasketItem: null,
  restoredParkedBasketName: "",
  hasProvidedPasscode: false,
  priceRange: { min: null, max: null },
};

export const contentViewStateMethods = (
  contentViewState: ContentViewState,
  setContentViewState
) => ({
  updateItem: (itemId: string, data: Partial<ContentItem>) => {
    const newItems = contentViewState.items.map((item) => {
      if (item.id === itemId) {
        return {
          ...item,
          ...data,
        };
      }
      return item;
    });

    setContentViewState((prev) => ({ ...prev, items: newItems }));
  },
});
